import { useQuery } from '@apollo/client';
import Router, { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { destroy } from 'redux-form';

import { DISABLE_BUILDER_QUERY_CHECK } from '@glass/common/modules/assignments/configs/disableBuilderQueryCheck';
import useAssignmentIsVariation from '@glass/common/modules/assignments/useAssignmentIsVariation';
import useIsAssignmentReady from '@glass/common/modules/assignments/useIsAssignmentReady';
import { DEFAULT_USER_ERROR } from '@glass/common/modules/errors/messages';
import { HOME_PATHNAME, PROFILE_PATHNAME } from '@glass/common/modules/pages/paths';
import {
  RESUME_ID_QUERY,
  SEED_QUERY,
  TEMPLATE_QUERY,
} from '@glass/common/modules/pages/queryParams';
import { selectIsUser } from '@glass/web/modules/auth';
import resetGlobalDialogAction from '@glass/web/modules/globalDialog/resetGlobalDialogAction';
import { PROMPT_LOGIN, PROMPT_RECOVER_ACCOUNT } from '@glass/web/modules/graphql/types';
import { RESUME_BUILDER_FORM_NAME } from '@glass/web/modules/reduxForm/formNames';
import { RESUME_V2_FIELD, TEMPLATE_FIELD_V2 } from '@glass/web/modules/resumes/fieldKeys';
import { selectResumeIdValue } from '@glass/web/modules/resumes/selectors';
import useQueryParamState from '@glass/web/modules/router/useQueryParamState';
import selectIsAuthReady from '@glass/web/modules/userConfig/selectors/selectIsAuthReady';

import usePrefetchNextBuilderStep from '@glass/shared/components/ResumeBuilder/usePrefetchNextBuilderStep';
import useBuilderQuery from '@glass/shared/components/hooks/useBuilderQuery';
import {
  RESET_PASSWORD_ACTION,
  ROCKET_LINK_ACTION,
  SIGNIN_ACTION,
} from '@glass/shared/modules/auth/authActionTypes';
import signOutUserAction from '@glass/shared/modules/auth/signOutUserAction';
import promptAuthAction from '@glass/shared/modules/authForm/actions/promptAuthAction';
import setAuthPropsAction from '@glass/shared/modules/authForm/actions/setAuthPropsAction';
import { EMAIL_AUTH_VIEW, MESSAGE_AUTH_VIEW } from '@glass/shared/modules/authForm/authViews';
import handleErrorActions from '@glass/shared/modules/errors/handleErrorActions';
import ensureValidBuilderStep from '@glass/shared/modules/resumes/ensureValidBuilderStep';
import useActiveResume from '@glass/shared/modules/resumes/hooks/useActiveResume';
import useNextStepParams from '@glass/shared/modules/resumes/hooks/useNextStepParams';
import useTemplate from '@glass/shared/modules/resumes/hooks/useTemplate';
import resumeQuery from '@glass/shared/modules/resumes/queries/resume.client.graphql';

const exitAction = () => (dispatch, getState) =>
  Router.replace(selectIsUser(getState()) ? PROFILE_PATHNAME : HOME_PATHNAME);

const AVAILABLE_AUTH_ACTIONS = [RESET_PASSWORD_ACTION, ROCKET_LINK_ACTION];

const EXPIRED_LINK_SETTINGS = {
  message: 'That link has expired, please enter your email and try again',
  actionType: ROCKET_LINK_ACTION,
  view: EMAIL_AUTH_VIEW,
};

const handleUnknownResumeErrorAction =
  ({ title, message } = {}) =>
  (dispatch) => {
    const settings = {
      onExit: () => dispatch(exitAction()),
      inDialog: true,
      title: title || `This $(resumeWords.abbreviated) is not accessible`,
      message: message || `Please try again from the device the resume was initially created on`,
      actionType: SIGNIN_ACTION,
      view: MESSAGE_AUTH_VIEW,
      availableActions: AVAILABLE_AUTH_ACTIONS,
    };

    return dispatch(promptAuthAction(settings));
  };

const createHandleResumeAuthAction =
  ({
    actionType = SIGNIN_ACTION,
    message = `This $(resumeWords.singular) is not accessible, please try again`,
    view = EMAIL_AUTH_VIEW,
  } = {}) =>
  (error, { refetch }) =>
  async (dispatch, getState) => {
    const onAuth = () => {
      return refetch({ fetchPolicy: 'network-only' })
        .then(({ errors }) => {
          if (errors) {
            throw new Error(errors[0]);
          }
          dispatch(resetGlobalDialogAction());
          dispatch(setAuthPropsAction());
        })
        .catch((err) => {
          if (process.env.NODE_ENV !== 'production') {
            console.error(err);
          }

          return dispatch(handleUnknownResumeErrorAction());
        });
    };

    // this didn't work so we need to log them out
    if (selectIsUser(getState())) {
      await dispatch(signOutUserAction({ path: null, snackbar: false }));
    }

    return dispatch(
      promptAuthAction({
        onExit: () => dispatch(exitAction()),
        inDialog: true,
        actionType,
        message,
        view,
        onAuth,
        availableActions: AVAILABLE_AUTH_ACTIONS,
      }),
    );
  };

const availableActions = {
  [PROMPT_LOGIN]: createHandleResumeAuthAction(),
  [PROMPT_RECOVER_ACCOUNT]: createHandleResumeAuthAction(EXPIRED_LINK_SETTINGS),
  // todo: authenticate with phone (resume and user)
};

const handleFetchResumeErrorAction = (apolloError, options) => (dispatch) => {
  dispatch(handleErrorActions(apolloError, availableActions, options)).catch((err) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error(err);
    }
    dispatch(
      handleUnknownResumeErrorAction({
        action: SIGNIN_ACTION,
        message: DEFAULT_USER_ERROR,
      }),
    );
  });
};

const useGetResume = () => {
  const {
    isReady,
    query: { [RESUME_ID_QUERY]: resumeId, [TEMPLATE_QUERY]: templateSlug, [SEED_QUERY]: seed },
  } = useRouter();

  const dispatch = useDispatch();
  const authReady = useSelector(selectIsAuthReady);
  const ready = isReady && authReady;

  const {
    data,
    loading: loadingResume,
    error,
    refetch,
  } = useQuery(resumeQuery, {
    variables: {
      id: resumeId,
    },
    skip: !ready || !resumeId,
  });

  const [handledError, setHandledError] = useState(null);
  useEffect(() => {
    if (error && !handledError) {
      setHandledError(error);
      dispatch(handleFetchResumeErrorAction(error, { refetch }));
    }
  }, [dispatch, error, handledError, refetch]);

  const shouldFetchTemplate = (isReady && !resumeId) || (ready && !loadingResume && !data?.resume);

  const {
    templateSlug: validatedTemplateSlug,
    template,
    loading: loadingTemplate,
  } = useTemplate({
    seedOverride: seed,
    slug: templateSlug,
    skip: !shouldFetchTemplate,
  });

  const templateResume = useMemo(() => {
    if (!template) {
      return null;
    }
    return {
      [RESUME_V2_FIELD]: {
        [TEMPLATE_FIELD_V2]: template,
      },
    };
  }, [template]);

  const loading = !authReady || loadingResume || loadingTemplate;

  return {
    resume: data?.resume || templateResume,
    validatedTemplateSlug,
    loading,
  };
};

// http://localhost:3000/resume-builder/checkout-beta?_ao=PAYMENT_BETA_PAGES:ACTIVE,DISABLE_BUILDER_QUERY_CHECK:ACTIVE
const useValidBuilderRedirect = (query) => {
  const { isReady } = useRouter();
  const isAssignmentReady = useIsAssignmentReady();
  const isDisableBuilderQueryCheck = useAssignmentIsVariation(DISABLE_BUILDER_QUERY_CHECK);

  useEffect(() => {
    if (!isReady || !isAssignmentReady) {
      return;
    }
    ensureValidBuilderStep(undefined, query, { checkQuery: !isDisableBuilderQueryCheck });
  }, [isAssignmentReady, isDisableBuilderQueryCheck, isReady, query]);
};

const useResumeBuilder = (props) => {
  const query = useBuilderQuery(props);
  const dispatch = useDispatch();
  const { [RESUME_ID_QUERY]: resumeId } = query;

  const { [TEMPLATE_QUERY]: templateSlug } = query;
  const { resume, validatedTemplateSlug, loading } = useGetResume();
  useValidBuilderRedirect(query);
  useQueryParamState(TEMPLATE_QUERY, validatedTemplateSlug || templateSlug);
  useActiveResume({ resume });
  const formResumeId = useSelector(selectResumeIdValue);
  const { isReady } = useRouter();

  const nextStepParams = useNextStepParams(query, resume);
  usePrefetchNextBuilderStep(nextStepParams);

  useEffect(() => {
    if (isReady && !!formResumeId && !!resumeId && formResumeId !== resumeId) {
      dispatch(destroy(RESUME_BUILDER_FORM_NAME));
    }
  }, [isReady, formResumeId, resumeId, dispatch]);

  return {
    query,
    resume,
    nextStepParams,
    loading,
  };
};

export default useResumeBuilder;
